// Settings Page - User settings and integrations
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Settings as SettingsIcon, ArrowLeft, ExternalLink, Check, X, Loader2,
  Link2, Unlink, AlertCircle
} from 'lucide-react';
import GoldenSpiral from '../components/GoldenSpiral';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../config/firebase';

// API base URL for Firebase Functions
const API_URL = 'https://us-central1-vincikit-bd9e1.cloudfunctions.net';

// Get fresh ID token from Firebase Auth
async function getIdToken() {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Not authenticated');
  }
  return await currentUser.getIdToken(true);
}

export default function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Notion connection state
  const [notionStatus, setNotionStatus] = useState({
    loading: true,
    connected: false,
    workspaceName: null,
    error: null
  });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Check for OAuth callback results
  useEffect(() => {
    const notionConnected = searchParams.get('notion_connected');
    const notionError = searchParams.get('notion_error');
    const workspace = searchParams.get('workspace');

    if (notionConnected === 'true' && workspace) {
      setNotionStatus({
        loading: false,
        connected: true,
        workspaceName: workspace,
        error: null
      });
      // Clear URL params
      navigate('/settings', { replace: true });
    } else if (notionError) {
      setNotionStatus(prev => ({
        ...prev,
        loading: false,
        error: decodeURIComponent(notionError)
      }));
      // Clear URL params
      navigate('/settings', { replace: true });
    }
  }, [searchParams, navigate]);

  // Check Notion connection status on load
  useEffect(() => {
    const checkNotionStatus = async () => {
      if (!user) return;

      try {
        const token = await getIdToken();
        const response = await fetch(`${API_URL}/notionStatus`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setNotionStatus({
            loading: false,
            connected: data.connected,
            workspaceName: data.workspaceName || null,
            error: null
          });
        } else {
          setNotionStatus({
            loading: false,
            connected: false,
            workspaceName: null,
            error: null
          });
        }
      } catch (error) {
        console.error('Failed to check Notion status:', error);
        setNotionStatus({
          loading: false,
          connected: false,
          workspaceName: null,
          error: null
        });
      }
    };

    checkNotionStatus();
  }, [user, getIdToken]);

  // Start Notion OAuth flow
  const handleConnectNotion = async () => {
    if (!user) return;

    setConnecting(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/notionOAuthStart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ source: 'web' })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Notion OAuth
        window.location.href = data.authUrl;
      } else {
        const error = await response.json();
        setNotionStatus(prev => ({
          ...prev,
          error: error.error || 'Failed to start OAuth flow'
        }));
        setConnecting(false);
      }
    } catch (error) {
      console.error('Failed to connect Notion:', error);
      setNotionStatus(prev => ({
        ...prev,
        error: 'Failed to connect to Notion'
      }));
      setConnecting(false);
    }
  };

  // Disconnect Notion
  const handleDisconnectNotion = async () => {
    if (!user || !window.confirm('Are you sure you want to disconnect Notion?')) return;

    setDisconnecting(true);
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_URL}/notionDisconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotionStatus({
          loading: false,
          connected: false,
          workspaceName: null,
          error: null
        });
      } else {
        const error = await response.json();
        setNotionStatus(prev => ({
          ...prev,
          error: error.error || 'Failed to disconnect'
        }));
      }
    } catch (error) {
      console.error('Failed to disconnect Notion:', error);
      setNotionStatus(prev => ({
        ...prev,
        error: 'Failed to disconnect Notion'
      }));
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b border-gray-200">
        <nav className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/home')}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
                <GoldenSpiral className="w-5 h-5" color="white" />
              </div>
              <span className="font-bold text-xl">VinciKit</span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <SettingsIcon className="w-5 h-5" />
            <span className="font-medium">Settings</span>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-600 mb-8">Manage your account and integrations</p>

            {/* Account Info */}
            <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-lg">
                    {user?.email?.[0]?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user?.email}</p>
                  <p className="text-sm text-gray-500">VinciKit Account</p>
                </div>
              </div>
            </section>

            {/* Integrations */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Integrations</h2>

              {/* Notion Integration */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {/* Notion Logo */}
                    <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                      <svg viewBox="0 0 100 100" className="w-6 h-6">
                        <path d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z" fill="#fff"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M61.35 0.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723 0.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257 -3.89c5.433 -0.387 6.99 -2.917 6.99 -7.193V20.64c0 -2.21 -0.873 -2.847 -3.443 -4.733L74.167 3.143c-4.273 -3.107 -6.02 -3.5 -12.817 -2.917zM25.92 19.523c-5.247 0.353 -6.437 0.433 -9.417 -1.99L8.927 11.507c-0.77 -0.78 -0.383 -1.753 1.557 -1.947l53.193 -3.887c4.467 -0.39 6.793 1.167 8.54 2.527l9.123 6.61c0.39 0.197 1.36 1.36 0.193 1.36l-54.933 3.307 -0.68 0.047zM19.803 88.3V30.367c0 -2.53 0.777 -3.697 3.103 -3.893L86 22.78c2.14 -0.193 3.107 1.167 3.107 3.693v57.547c0 2.53 -0.39 4.67 -3.883 4.863l-60.377 3.5c-3.493 0.193 -5.043 -0.97 -5.043 -4.083zm59.6 -54.827c0.387 1.75 0 3.5 -1.75 3.7l-2.91 0.577v42.773c-2.527 1.36 -4.853 2.137 -6.797 2.137 -3.107 0 -3.883 -0.973 -6.21 -3.887l-19.03 -29.94v28.967l6.02 1.363s0 3.5 -4.857 3.5l-13.39 0.777c-0.39 -0.78 0 -2.723 1.357 -3.11l3.497 -0.97v-38.3L30.48 40.667c-0.39 -1.75 0.58 -4.277 3.3 -4.473l14.367 -0.967 19.8 30.327v-26.83l-5.047 -0.58c-0.39 -2.143 1.163 -3.7 3.103 -3.89l13.4 -0.78z" fill="#000"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Notion</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Connect Notion to import PRDs and export tech stack recommendations
                      </p>

                      {/* Status */}
                      {notionStatus.loading ? (
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Checking connection...
                        </div>
                      ) : notionStatus.connected ? (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <Check className="w-3 h-3" />
                            Connected
                          </span>
                          <span className="text-sm text-gray-600">
                            {notionStatus.workspaceName}
                          </span>
                        </div>
                      ) : notionStatus.error ? (
                        <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          {notionStatus.error}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div>
                    {notionStatus.loading ? null : notionStatus.connected ? (
                      <button
                        onClick={handleDisconnectNotion}
                        disabled={disconnecting}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {disconnecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Unlink className="w-4 h-4" />
                        )}
                        Disconnect
                      </button>
                    ) : (
                      <button
                        onClick={handleConnectNotion}
                        disabled={connecting}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {connecting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4" />
                        )}
                        Connect
                      </button>
                    )}
                  </div>
                </div>

                {/* Features */}
                {notionStatus.connected && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-600 mb-2">What you can do:</p>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Import PRDs and project briefs from Notion
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Export tech stack recommendations as Notion pages
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Use via MCP in Claude Desktop or Cursor
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </section>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <button
                onClick={() => navigate('/home')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
